/**
 * Shared recovery policy for the AI image scanners (label, meal-photo,
 * equipment, workout-plan, recipe). Given a failed scan, it decides whether the
 * scanner should keep the captured photos and offer an in-place "Try Again", or
 * reset to a fresh camera. It owns the *behavior* only — each scanner still picks
 * its own title/message copy for the alert.
 *
 * Every scanner's server interface uses the SAME error-code strings for the
 * transient provider-side failures (see scan-label-interface.ts,
 * scan-equipment-interface.ts, scan-workout-plan-interface.ts,
 * scan-recipe-interface.ts, and meal-photo which reuses these strings), so this
 * helper matches the raw string values and needs no per-scanner enum.
 */

/** The transient error codes shared by every scanner interface. Retrying the
 *  same photos usually succeeds once the provider / network recovers. */
const PROVIDER_ERROR = 'provider_error';
const PROVIDER_TIMEOUT = 'provider_timeout';
const INCOMPLETE_RESPONSE = 'incomplete_response';

/**
 * Which title/message pair a scanner shows for a failed scan.
 * Note: 'provider' serves double duty — it is the copy class for a retryable
 * provider failure AND the fallback class for genuinely non-retryable errors;
 * the policy's keepPhotos / offerRetry fields, not the kind, are what separate
 * those two cases.
 */
export type ScanErrorAlertKind = 'offline' | 'incomplete' | 'provider';

export interface ScanErrorPolicy {
  /** which title/message class to show */
  kind: ScanErrorAlertKind;
  /** keep the captured photos so the user can retry in place */
  keepPhotos: boolean;
  /** show a "Try Again" button that re-runs the scan */
  offerRetry: boolean;
}

/**
 * Decide how a scanner should react to a failed scan.
 *
 * Transient failures keep the captured photos and offer an in-place retry:
 * - the device is offline (the request never had a chance),
 * - the provider failed (`provider_error`) — in Multi mode this is only
 *   surfaced after every model in the list was already tried and blacklisted,
 *   so retrying lets the user try again once the providers/network recover,
 * - the model response timed out or came back incomplete (`provider_timeout` /
 *   `incomplete_response`) — retrying the same images usually succeeds.
 *
 * Genuine non-retryable errors (too many images, image too large, unknown
 * model, all models restricted, auth required, no image, unsupported type)
 * keep the "start over" behavior: retrying the same photos cannot help, so the
 * camera resets and no retry button is offered.
 */
export function getScanErrorPolicy(
  // a scan result's `error` field is loosely typed as a string; the shared
  // transient codes are matched in the switch below
  error: string | undefined,
  isOnline: boolean,
): ScanErrorPolicy {
  // if offline, the request never reached the server — keep photos and let the
  // user retry once they reconnect
  if (!isOnline) return { kind: 'offline', keepPhotos: true, offerRetry: true };

  switch (error) {
    case PROVIDER_TIMEOUT:
    case INCOMPLETE_RESPONSE:
      return { kind: 'incomplete', keepPhotos: true, offerRetry: true };
    case PROVIDER_ERROR:
      return { kind: 'provider', keepPhotos: true, offerRetry: true };
    default:
      // any other error code (too_many_images, image_too_large, unknown_model,
      // all_models_restricted, auth_required, no_image, unsupported_equipment_type,
      // or an unknown/future string) — retrying the same photos won't fix these
      return { kind: 'provider', keepPhotos: false, offerRetry: false };
  }
}

/**
 * The three-step recovery every AI scanner page runs when a scan fails: ask the
 * policy above what to do, show the page's own alert with that answer, and
 * re-run the scan if the user tapped "Try Again".
 *
 * Each page used to hold its own copy of this, comment included (deferred
 * finding df-d3b3b23b9d34). The alert itself stays with the page: the five
 * scanners genuinely differ in what they show — which page phase they reset,
 * when they repaint, whether they hold an alert-open flag, and whether they
 * reset one photo or all of them — and
 * `design-docs/scanner-shared-components.md` records those error surfaces as
 * deliberate per-scanner choices.
 *
 * The caller must have already stopped its progress bar.
 */
export async function runScanFailureRecoveryAsync(options: {
  /** the failed scan's error code, or undefined when the call threw */
  error: string | undefined;
  isOnline: boolean;
  /**
   * Show the page's own scan-error alert; resolves to 'retry' when tapped. The
   * policy is passed as well for the one scanner that picks its title and
   * message from `policy.kind`; the others only need the alert options.
   */
  showScanErrorAsync: (
    alertOptions: { resetCamera: boolean; offerRetry: boolean },
    policy: ScanErrorPolicy,
  ) => Promise<'retry' | null>;
  /**
   * run the callback on a fresh task — pages pass `run => this.safeTimeout(run, 0)`.
   * The retry MUST be deferred rather than run here: this recovery is awaited
   * from inside the original analyze call, whose `finally` still has to release
   * the scanner's wake lock. Running the retry straight away would let that
   * release drop the lock the retry has just re-acquired, because both use the
   * same tag. Deferring to a macrotask lets the original call unwind through its
   * `finally` first, so the retry then cleanly re-acquires the lock.
   */
  scheduleOnNextTask: (run: () => void) => void;
  /** start the scan again */
  retryScan: () => void;
}): Promise<void> {
  const policy = getScanErrorPolicy(options.error, options.isOnline);
  const action = await options.showScanErrorAsync({
    resetCamera: !policy.keepPhotos,
    offerRetry: policy.offerRetry,
  }, policy);
  if (action === 'retry') options.scheduleOnNextTask(() => options.retryScan());
}
